import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { Button } from '@/components/ui/button';
import {
	Edit,
	Phone,
	Mail,
	FileText,
	Send,
	Loader2,
	Tag,
	House,
	Link,
	ExternalLink,
	Contact,
} from 'lucide-react'; // Import Send icon and Loader
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CustomerDialog } from './CustomerDialog';
import { CustomerPhotoEditor } from '@/components/customers/CustomerPhotoEditor';
import {
	CreatePipelineItemButton,
	CreatePipelineItemDialog,
} from './CreatePipelineItemDialog';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { MASKED_PHONE } from '@/utils/maskPhone';
import { isTouchViewport, useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { useSelector } from 'react-redux';
import axios from 'axios';
import getWebchatApiUrl from '@/utils/getWebchatUrl';
import { notifyConversationAccessDenied } from '@/utils/conversationAccessDenied';
import { buildCanonicalCustomerConversationPath } from '@/utils/customerConversationRoutes';
import { useDispatch } from 'react-redux';
import {
	setOpenOrClosedConversationsSelected,
	addConversationToCustomerConversations,
} from '@/actions/webchat';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { setVisible, setCustomer, setStatus } from '@/reducers/voiceReducer';
import { isCustomerOnActiveCall } from '@/utils/activeCustomerCall';
const webchatApiUrl = getWebchatApiUrl(window.location.href);

// import { Badge } from '../TagBadge';
interface CustomerInfoProps {
	customerInfo: any;
	isLoading: boolean;
	isInSidebar?: boolean;
	isLoadingDetails?: boolean;
	/**
	 * Opens an edit dialog the parent renders instead of this card's own, so
	 * the contact page can keep it open while it swaps between phone and
	 * desktop layouts.
	 */
	onEdit?: () => void;
	/** Same for the card's new pipeline item dialog. */
	onCreatePipelineItem?: () => void;
}

/**
 * Opens the customer's conversation, loading it first when it isn't in the
 * inbox list yet. Shared by the desktop card and the phone contact screen.
 */
export const useOpenCustomerConversation = (customerInfo: any) => {
	const { customerConversations } = useSelector(
		(state: any) => state.webchat,
	);
	const [fetching, setFetching] = useState<boolean>(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const openConversation = async (): Promise<void> => {
		// get the customer conversation id

		const { _id: _customer } = customerInfo;

		const customerConversation = customerConversations.find(
			// group conversations have _customers[] and no _customer
			(conversation: any) => conversation._customer?._id === _customer,
		);

		if (!customerConversation) {
			setFetching(true);
			const newDataRes = await axios(
				`${webchatApiUrl}/api/webchat/get-messages-from-customer`,
				{
					method: 'post',
					data: { _customer },
					withCredentials: true,
				},
			);

			if (newDataRes.data.error) {
				setFetching(false);
				if (newDataRes.data.accessDenied) {
					notifyConversationAccessDenied();
					return;
				}
				toast.error('Error getting customer messages');
				return;
			}

			setFetching(false);

			const { customerConversation } = newDataRes.data;

			addConversationToCustomerConversations(customerConversation, true);

			const openConversationsSelected = customerConversation.open;

			dispatch(
				setOpenOrClosedConversationsSelected(openConversationsSelected),
			);

			navigate(
				buildCanonicalCustomerConversationPath({
					_customerConversation: customerConversation._id,
					_business: customerInfo._business,
				}),
			);
		} else {
			navigate(
				buildCanonicalCustomerConversationPath({
					_customerConversation: customerConversation._id,
					_business: customerInfo._business,
				}),
			);
		}
	};

	return { openConversation, fetching };
};

export const CustomerInfo: React.FC<CustomerInfoProps> = ({
	customerInfo,
	isLoading,
	isInSidebar = false,
	isLoadingDetails = false,
	onEdit,
	onCreatePipelineItem,
}) => {
	const maskPhone = usePhoneMask();
	// Phones (and other touch screens) call or email from the device; a mouse
	// click on desktop keeps selecting the text as before.
	const linkContacts = useIsMobile() || isTouchViewport();
	const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
	const { status: voiceStatus, ongoingCalls } = useSelector(
		(state: any) => state.voice,
	);
	const dispatch = useDispatch();
	const navigate = useNavigate(); // Initialize navigate for routing

	const handleEdit = (): void => {
		if (onEdit) return onEdit();
		// Toggle between editing and viewing
		setIsOpenDialog(!isOpenDialog);
	};

	const callCustomer = async (): Promise<void> => {
		// Starting a call mid-call would repoint the call bar at this customer
		// and put it back in 'connecting' while the first call is still live.
		if (['connecting', 'ringing', 'answered'].includes(voiceStatus)) {
			toast.error('Finish the current call before starting another one.');
			return;
		}
		if (isCustomerOnActiveCall(ongoingCalls, customerInfo)) {
			toast.error('This customer is already on a call with another teammate.');
			return;
		}

		dispatch(setCustomer(customerInfo));
		dispatch(setStatus('connecting'));
		dispatch(setVisible(true));
	};

	const { openConversation: handleTextClick, fetching } =
		useOpenCustomerConversation(customerInfo);

	if (isLoading) {
		if (isInSidebar) {
			return (
				<div className="shrink-0 border-b border-border/60 px-4 py-6" aria-label="Loading customer details" aria-busy="true">
					<div className="flex flex-col items-center gap-3">
						<Skeleton className="size-16 rounded-full bg-muted/50" />
						<Skeleton className="h-4 w-28 bg-muted/50" />
					</div>
				</div>
			);
		}

		return (
			<Card>
				<CardHeader className="pb-4">
					<CardTitle className="flex items-center justify-between">
						<Skeleton className="h-8 w-64" />
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col gap-2">
						<div className="mt-2">
							<Skeleton className="h-6 w-32 mb-2" />
						</div>
						<div className="flex items-center">
							<div className="flex items-center space-x-2 w-full">
								<Skeleton className="h-4 w-4" />
								<Skeleton className="h-4 w-40 flex-1" />
							</div>
						</div>
						<Skeleton className="h-9 w-20" />
					</div>
				</CardContent>
			</Card>
		);
	}

	// Touch screens: phone and email open the dialer and mail app (a masked
	// number on a deactivated account stays plain text). Desktop keeps the
	// plain text.
	const shownPhone = maskPhone(formatPhoneForUser(customerInfo.phone));
	const canDial =
		linkContacts && Boolean(customerInfo.phone) && maskPhone(customerInfo.phone) !== MASKED_PHONE;
	const contactLinkBaseClass =
		'rounded-sm text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:opacity-60 dark:text-blue-400';
	const telHref = `tel:${String(customerInfo.phone || '').replace(/[^\d+]/g, '')}`;

	if (isInSidebar) {
		const name = customerInfo.name || 'No name';
		const detailRow = (Icon: typeof Phone, label: string, value: React.ReactNode, className?: string) => (
			<div className={cn('grid grid-cols-[16px_58px_minmax(0,1fr)] items-start gap-x-2.5 text-sm', className)}>
				<Icon className="mt-0.5 size-3.5 text-muted-foreground" aria-hidden="true" />
				<dt className="text-muted-foreground">{label}</dt>
				<dd className="min-w-0 break-words text-foreground">{value}</dd>
			</div>
		);
		// Phones get 44px phone and email rows. The link stays a block (not
		// flex) so a long email still wraps inside the column.
		const contactRowClass = 'max-md:min-h-11 max-md:items-center';
		const contactLinkClass = cn(contactLinkBaseClass, 'max-md:block max-md:min-h-11 max-md:py-3');
		return (
			<>
				{isOpenDialog && (
					<CustomerDialog
						open={isOpenDialog}
						setOpen={setIsOpenDialog}
						customerInfo={customerInfo}
						createCustomerAndLinkToProject={undefined}
						_project={undefined}
					/>
				)}
				<div className="shrink-0 border-b border-border/60">
					<div className="flex flex-col items-center px-4 pb-4 pt-6 text-center">
						<CustomerPhotoEditor
							customer={customerInfo}
							className="size-16"
							fallbackClassName="text-xl font-medium"
						/>
						<button
							type="button"
							onClick={() => navigate(`/customers/${customerInfo._id}`)}
							className="mt-3 max-w-full rounded-sm text-base font-medium text-foreground break-words hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						>
							{name}
						</button>
						<div className="mt-3 flex items-center justify-center gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={handleEdit}
								disabled={isLoadingDetails}
								className="h-8 gap-1.5 rounded-lg border-border/70 bg-muted/30 font-normal shadow-none max-md:min-h-11"
							>
								<Edit className="size-3.5" />
								Edit
							</Button>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => navigate(`/customers/${customerInfo._id}`)}
								className="h-8 gap-1.5 rounded-lg border-border/70 bg-muted/30 font-normal shadow-none max-md:min-h-11"
							>
								<Contact className="size-3.5" />
								View details
							</Button>
						</div>
					</div>
					<dl className="space-y-2.5 border-t border-border/60 px-4 py-4">
						{detailRow(
							Phone,
							'Phone',
							shownPhone && canDial ? (
								<a href={telHref} className={contactLinkClass}>
									{shownPhone}
								</a>
							) : (
								shownPhone || <span className="text-muted-foreground">Not provided</span>
							),
							shownPhone ? contactRowClass : undefined,
						)}
						{customerInfo.email &&
							detailRow(
								Mail,
								'Email',
								linkContacts ? (
									<a href={`mailto:${customerInfo.email}`} className={contactLinkClass}>
										{customerInfo.email}
									</a>
								) : (
									customerInfo.email
								),
								contactRowClass,
							)}
						{customerInfo.details && detailRow(FileText, 'Details', customerInfo.details)}
						{customerInfo.tags?.filter(Boolean).length > 0 &&
							detailRow(
								Tag,
								'Tags',
								<div className="flex flex-wrap gap-1">
									{customerInfo.tags.filter(Boolean).map((tag: string) => (
										<Badge
											key={tag}
											variant="secondary"
											className="rounded-full text-xs font-normal"
										>
											{tag}
										</Badge>
									))}
								</div>,
							)}
						{customerInfo.associatedLinks?.some((link: any) => link.name && link.url) &&
							detailRow(
								Link,
								'Links',
								<div className="flex flex-col gap-1">
									{customerInfo.associatedLinks
										.filter((link: any) => link.name && link.url)
										.map((link: any, index: number) => (
											<a
												key={index}
												href={/^https?:\/\//.test(link.url) ? link.url : `https://${link.url}`}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1 break-all text-blue-600 hover:underline dark:text-blue-400"
											>
												{link.name}
												<ExternalLink className="size-3 shrink-0" />
											</a>
										))}
								</div>,
							)}
					</dl>
				</div>
			</>
		);
	}

	const Wrapper = Card;

	return (
		<>
			{isOpenDialog && (
				<CustomerDialog
					open={isOpenDialog}
					setOpen={setIsOpenDialog}
					customerInfo={customerInfo}
					createCustomerAndLinkToProject={undefined}
					_project={undefined}
				/>
			)}
			<Wrapper>
				<div
					className={cn(
						isInSidebar ? 'px-4 pt-4 pb-2' : 'px-6 pt-6 pb-2',
					)}
				>
					<div className="flex items-center justify-between gap-3">
						<CustomerPhotoEditor customer={customerInfo} className="h-16 w-16 text-xl" />
						<button
							onClick={() => {
								navigate(`/customers/${customerInfo._id}`);
							}}
							className={cn(
								'min-w-0 flex-1 font-bold text-indigo-600 hover:text-indigo-700 transition-colors duration-200 break-words hover:underline text-left',
								isInSidebar ? 'text-lg pr-8' : 'text-2xl',
							)}
						>
							{customerInfo.name || 'No Name'}
						</button>

						{!isInSidebar && (
							<div className="flex items-center gap-2">
								<button
									onClick={handleTextClick}
									className="relative inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
									disabled={fetching}
								>
									{fetching ? (
										<Loader2 className="animate-spin h-4 w-4 mr-2" />
									) : (
										<Send size={14} className="mr-2" />
									)}
									{fetching ? 'Loading...' : 'Send Message'}
								</button>
							</div>
						)}
					</div>
				</div>
				<div className={cn(isInSidebar ? 'px-4 pb-4' : 'px-6 pb-6')}>
					<div
						className={cn('space-y-4', isInSidebar && 'space-y-2')}
					>
						{/* Main Customer Information - Single Column */}
						<div
							className={cn(
								'space-y-0',
								isInSidebar && 'space-y-1',
							)}
						>
							{/* Phone */}
							<div
								className={cn(
									'flex items-center space-x-3 rounded-lg transition-colors',
									isInSidebar
										? 'space-x-2'
										: 'p-3 hover:bg-gray-50 dark:hover:bg-gray-800',
								)}
							>
								<div className="flex-shrink-0">
									<Phone
										size={16}
										className="text-gray-500"
									/>
								</div>
								<div className="flex-1 min-w-0">
									<p
										className={cn(
											'font-medium text-gray-900 dark:text-gray-100',
											isInSidebar
												? 'text-sm truncate'
												: 'text-sm',
										)}
									>
										{canDial && shownPhone ? (
											<a href={telHref} className={contactLinkBaseClass}>
												{shownPhone}
											</a>
										) : shownPhone || (
											<span className="text-gray-400 italic">
												{isInSidebar
													? 'no phone'
													: 'No phone provided'}
											</span>
										)}
									</p>
								</div>
							</div>

							{/* Email */}
							{(!isInSidebar || customerInfo.email) && (
								<div
									className={cn(
										'flex items-center space-x-3 rounded-lg transition-colors',
										isInSidebar
											? 'space-x-2'
											: 'p-3 hover:bg-gray-50 dark:hover:bg-gray-800',
									)}
								>
									<div className="flex-shrink-0">
										<Mail
											size={16}
											className="text-gray-500"
										/>
									</div>
									<div className="flex-1 min-w-0">
										<p
											className={cn(
												'font-medium text-gray-900 dark:text-gray-100',
												isInSidebar
													? 'text-sm truncate'
													: 'text-sm',
											)}
										>
											{customerInfo.email && linkContacts ? (
												<a href={`mailto:${customerInfo.email}`} className={contactLinkBaseClass}>
													{customerInfo.email}
												</a>
											) : customerInfo.email || (
												<span className="text-gray-400 italic">
													{isInSidebar
														? 'no email'
														: 'No email provided'}
												</span>
											)}
										</p>
									</div>
								</div>
							)}

							{/* Details */}
							{(!isInSidebar || customerInfo.details) && (
								<div
									className={cn(
										'flex items-start space-x-3 rounded-lg transition-colors',
										isInSidebar
											? 'space-x-2'
											: 'p-3 hover:bg-gray-50 dark:hover:bg-gray-800',
									)}
								>
									<div className="flex-shrink-0 mt-0.5">
										<FileText
											size={16}
											className="text-gray-500"
										/>
									</div>
									<div className="flex-1 min-w-0">
										<p
											className={cn(
												'font-medium text-gray-900 dark:text-gray-100',
												isInSidebar
													? 'text-sm'
													: 'text-sm',
											)}
										>
											{customerInfo.details || (
												<span className="text-gray-400 italic">
													No miscellaneous details
												</span>
											)}
										</p>
									</div>
								</div>
							)}

							{/* Tags */}
							{(!isInSidebar ||
								(customerInfo.tags &&
									customerInfo.tags.length > 0)) && (
								<div
									className={cn(
										'flex items-start space-x-3 rounded-lg transition-colors',
										isInSidebar
											? 'space-x-2'
											: 'p-3 hover:bg-gray-50 dark:hover:bg-gray-800',
									)}
								>
									<div className="flex-shrink-0 mt-0.5">
										<Tag
											size={16}
											className="text-gray-500"
										/>
									</div>
									<div className="flex-1 min-w-0">
										{customerInfo.tags &&
										customerInfo.tags.length ? (
											<div className="flex flex-wrap gap-2">
												{customerInfo.tags
													.filter(
														(tag: string) =>
															tag !== null,
													)
													.map((tag: string) => (
														<Badge
															variant="secondary"
															key={tag}
															className="text-xs"
														>
															{tag}
														</Badge>
													))}
											</div>
										) : (
											<p className="text-sm text-gray-400 italic">
												{isInSidebar
													? 'no tags'
													: 'No tags assigned'}
											</p>
										)}
									</div>
								</div>
							)}

							{/* Associated Links */}
							{(!isInSidebar ||
								(customerInfo.associatedLinks &&
									customerInfo.associatedLinks.length >
										0)) && (
								<div
									className={cn(
										'flex items-start space-x-3 rounded-lg transition-colors',
										isInSidebar
											? 'space-x-2'
											: 'p-3 hover:bg-gray-50 dark:hover:bg-gray-800',
									)}
								>
									<div className="flex-shrink-0 mt-0.5">
										<Link
											size={16}
											className="text-gray-500"
										/>
									</div>
									<div className="flex-1 min-w-0">
										{customerInfo.associatedLinks &&
										customerInfo.associatedLinks.length >
											0 ? (
											<div className="flex flex-wrap gap-2">
												{customerInfo.associatedLinks
													.filter(
														(link: any) =>
															link.name &&
															link.url,
													)
													.map(
														(
															link: any,
															index: number,
														) => {
															// Ensure URL is absolute
															const absoluteUrl =
																link.url.startsWith(
																	'http://',
																) ||
																link.url.startsWith(
																	'https://',
																)
																	? link.url
																	: `https://${link.url}`;

															return (
																<a
																	key={index}
																	href={
																		absoluteUrl
																	}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200 hover:underline"
																>
																	<span>
																		{
																			link.name
																		}
																	</span>
																	<ExternalLink
																		size={
																			12
																		}
																		className="flex-shrink-0"
																	/>
																</a>
															);
														},
													)}
											</div>
										) : (
											<p className="text-sm text-gray-400 italic">
												{isInSidebar
													? 'no links'
													: 'No links'}
											</p>
										)}
									</div>
								</div>
							)}
						</div>

						{/* Billing Information Section - Only show if not in sidebar */}
						{!isInSidebar && (
							<div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
								<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
									Billing Information
								</h3>
								<div className="space-y-0">
									<div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
										<div className="flex-shrink-0">
											<Mail
												size={16}
												className="text-gray-500"
											/>
										</div>
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
												{customerInfo.billingEmail || (
													<span className="text-gray-400 italic">
														No billing email set
													</span>
												)}
											</p>
										</div>
									</div>
									<div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
										<div className="flex-shrink-0 mt-0.5">
											<House
												size={16}
												className="text-gray-500"
											/>
										</div>
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
												{customerInfo.billingAddress || (
													<span className="text-gray-400 italic">
														No billing address set
													</span>
												)}
											</p>
										</div>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Action Buttons */}
					<div
						className={cn(
							'flex items-center justify-between',
							isInSidebar ? 'pt-2 mt-2' : 'pt-6 mt-6',
						)}
					>
						<div className="flex items-center space-x-3">
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											onClick={handleEdit}
											size="sm"
											variant="outline"
											disabled={isLoading}
											aria-label="Edit contact"
											data-opens="edit-contact"
											// The contact page's Edit dialog hands focus back here
											// after a mouse close too (returnFocusTo), and Radix opens
											// the tooltip on any focus but a click. ui/tooltip already
											// keeps it shut on touch screens; keep it shut with a mouse
											// as well, so only a keyboard user sees it.
											onFocus={(event) => {
												if (!event.currentTarget.matches(':focus-visible'))
													event.preventDefault();
											}}
											className={cn(
												'flex items-center justify-center gap-1.5 max-[1569px]:w-9 max-[1569px]:h-9 max-[1569px]:p-0',
											)}
										>
											<Edit size={12} />

											<span className="hidden min-[1570px]:inline">
												Edit
											</span>
										</Button>
									</TooltipTrigger>
									<TooltipContent
										className={cn(
											!isInSidebar &&
												'min-[1570px]:hidden',
										)}
									>
										<p>Edit</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
							{!isInSidebar &&
								(onCreatePipelineItem ? (
									<CreatePipelineItemButton onClick={onCreatePipelineItem} />
								) : (
									<CreatePipelineItemDialog
										_customer={customerInfo._id}
									/>
								))}
							{!isInSidebar && (
								<Button
									variant="outline"
									size="sm"
									onClick={handleTextClick}
									disabled={fetching}
									className="flex items-center space-x-2"
								>
									<Send size={14} />
									<span>Send Message</span>
								</Button>
							)}
						</div>
						{isInSidebar && (
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="outline"
											size="sm"
											onClick={() => {
												navigate(
													`/customers/${customerInfo._id}`,
												);
											}}
											className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
										>
											<Contact size={16} />
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										<p>View Details</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						)}
					</div>
				</div>
			</Wrapper>
		</>
	);
};
